import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export type FeedbackContext = 'demographics' | 'annotation' | 'completion';

interface FeedbackWidgetProps {
  className?: string;
  position?: 'fixed' | 'relative';
  context: FeedbackContext;
  sessionId?: string;
  onFeedbackSubmit?: (feedback: string) => void;
  maxLength?: number;
}

/**
 * Context detection utility to determine current annotation context
 */
export function detectFeedbackContext(): FeedbackContext {
  const path = window.location.pathname;
  
  if (path.includes('/demographics')) {
    return 'demographics';
  } else if (path.includes('/annotation')) {
    return 'annotation';
  } else if (path.includes('/complete')) {
    return 'completion';
  }
  
  // Default fallback based on current route
  return 'annotation';
}

/**
 * Get context-specific placeholder text and title
 */
function getContextInfo(context: FeedbackContext) {
  switch (context) {
    case 'demographics':
      return {
        title: 'Demographics Feedback',
        placeholder: 'Share your thoughts about the demographic questions or any issues you encountered...'
      };
    case 'annotation':
      return {
        title: 'Annotation Feedback',
        placeholder: 'Tell us about your annotation experience, any technical issues, or suggestions for improvement...'
      };
    case 'completion':
      return {
        title: 'Session Feedback',
        placeholder: 'How was your overall experience? Any final thoughts or suggestions...'
      };
    default:
      return {
        title: 'Share Your Feedback',
        placeholder: 'Tell us about your experience, any issues, or suggestions...'
      };
  }
}

/**
 * Combined feedback widget with button and popover
 * Implements requirements 1.1, 1.2, 1.3, 1.4, 1.5, 2.1, 2.2, 2.3, 7.1, 7.2, 7.3, 7.4, 7.5
 */
export function FeedbackWidget({ 
  className, 
  position = 'fixed', 
  context, 
  sessionId,
  onFeedbackSubmit,
  maxLength = 500
}: FeedbackWidgetProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { title, placeholder } = getContextInfo(context);

  // Focus textarea when popover opens
  useEffect(() => {
    if (isPopoverOpen && textareaRef.current) {
      // Small delay to ensure popover is fully rendered
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isPopoverOpen]);

  // Reset form when popover closes
  useEffect(() => {
    if (!isPopoverOpen) {
      // Reset form state after close animation
      setTimeout(() => {
        setFeedback('');
        setSubmitStatus('idle');
        setErrorMessage('');
      }, 200);
    }
  }, [isPopoverOpen]);

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      setErrorMessage('Please enter some feedback before submitting.');
      return;
    }

    if (feedback.length > maxLength) {
      setErrorMessage(`Feedback must be ${maxLength} characters or less.`);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Call the parent callback if provided
      onFeedbackSubmit?.(feedback.trim());
      
      // TODO: This will be implemented when API endpoints are ready
      // For now, just log the feedback
      console.log('Feedback submitted:', {
        feedback: feedback.trim(),
        context,
        sessionId,
        timestamp: new Date().toISOString()
      });
      
      setSubmitStatus('success');
      
      // Auto-close after success message
      setTimeout(() => {
        setIsPopoverOpen(false);
      }, 1500);
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Ctrl/Cmd + Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
    // Close on Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsPopoverOpen(false);
    }
  };

  const remainingChars = maxLength - feedback.length;
  const isOverLimit = remainingChars < 0;
  const canSubmit = feedback.trim().length > 0 && !isOverLimit && !isSubmitting;

  const buttonClasses = cn(
    // Base button styling
    "gap-2 shadow-lg hover:shadow-xl transition-all duration-200",
    // Position-specific styling
    position === 'fixed' && [
      "fixed z-50",
      "bottom-4 right-4", // Default fixed positioning
      "md:bottom-6 md:right-6" // Responsive positioning
    ],
    className
  );

  return (
    <>
      {/* Feedback Button */}
      <Button
        onClick={() => setIsPopoverOpen(true)}
        className={buttonClasses}
        size="default"
        aria-label="Provide feedback about your annotation experience"
        title="Share feedback"
        // Accessibility attributes
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsPopoverOpen(true);
          }
        }}
      >
        <MessageCircle className="h-4 w-4" />
        <span className="hidden sm:inline">Feedback</span>
      </Button>

      {/* Feedback Popover */}
      {isPopoverOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div 
            className="bg-popover text-popover-foreground w-full max-w-md rounded-lg border shadow-lg animate-in fade-in-0 zoom-in-95 duration-200"
            role="dialog"
            aria-labelledby="feedback-title"
            aria-describedby="feedback-description"
          >
            <div className="p-6 space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 id="feedback-title" className="font-semibold text-lg">
                    {title}
                  </h3>
                  <p id="feedback-description" className="text-sm text-muted-foreground">
                    Help us improve your annotation experience
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setIsPopoverOpen(false)}
                  aria-label="Close feedback form"
                  disabled={isSubmitting}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-md">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-700 dark:text-green-400">
                    Thank you for your feedback!
                  </span>
                </div>
              )}

              {/* Error Message */}
              {(submitStatus === 'error' || errorMessage) && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-700 dark:text-red-400">
                    {errorMessage || 'Something went wrong. Please try again.'}
                  </span>
                </div>
              )}

              {/* Form */}
              {submitStatus !== 'success' && (
                <>
                  <div className="space-y-2">
                    <Textarea
                      ref={textareaRef}
                      placeholder={placeholder}
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className={cn(
                        "min-h-24 resize-none",
                        isOverLimit && "border-red-500 focus-visible:border-red-500"
                      )}
                      disabled={isSubmitting}
                      aria-describedby="char-count"
                    />
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">
                        Optional feedback • Press Ctrl+Enter to submit
                      </span>
                      <span 
                        id="char-count"
                        className={cn(
                          "text-muted-foreground",
                          isOverLimit && "text-red-500",
                          remainingChars <= 50 && remainingChars > 0 && "text-yellow-600"
                        )}
                      >
                        {remainingChars} characters remaining
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setIsPopoverOpen(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      className="gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Submit Feedback
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Auto-detecting feedback widget that determines context from current route
 */
export function AutoFeedbackWidget(props: Omit<FeedbackWidgetProps, 'context'>) {
  const context = detectFeedbackContext();
  
  return <FeedbackWidget {...props} context={context} />;
}